import React from "react";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
  SettingsOptionHeadingInline,
  SettingsOptionHeadingLight,
} from "../../styles";
import { useTranslation } from "react-i18next";
import { usePeopleStore } from "../../../../../../stores/use-people-store";
import { ButtonGroup, Select } from "@caelor/ui";
import { ContentLoadSettings } from "./defaults";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import MissingAppPanel from "../../../../../../components/ui/missing-app-panel/MissingAppPanel";
import useCosmosCollections from "../../../../../../hooks/use-cosmos-collections";
import Dynamic from "./enrichment/Dynamic";
import Manual from "./enrichment/Manual";

const buildTabOptions = (tabs) => {
  return tabs
    .sort((a, b) => a.position - b.position)
    .map((tab) => ({
      label: tab.name,
      value: tab.id,
    }));
};

function ContentSettings() {
  const { t } = useTranslation();

  const { data, selectedTab, setSelectedTab, updateConfigTab } = usePeopleStore(
    (state) => ({
      data: state.data,
      selectedTab: state.getSelectedTab(),
      setSelectedTab: state.setSelectedTab,
      updateConfigTab: state.updateConfigTab,
    })
  );

  const isCosmosInstalled = useOpticsStore((state) => state.isCosmosInstalled);

  const { collections } = useCosmosCollections();

  const areTabsActive = data.config.showTabs;

  const tabOptions = buildTabOptions(data.tabs);

  return (
    <>
      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("content-setup" + (areTabsActive ? "-tabs" : ""))}
        </SettingsOptionHeading>

        {areTabsActive && (
          <OptionContainer direction="column">
            <OptionLabel>{t("select-tab")}</OptionLabel>
            <OptionContent full>
              <Select
                options={tabOptions}
                value={tabOptions.find(({ value }) => value === selectedTab)}
                onChange={({ value }) => {
                  setSelectedTab(value);
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}

        <OptionContainer direction="column">
          {areTabsActive && (
            <OptionLabel alignToTop>{t("how-set-tabs")}</OptionLabel>
          )}
          <OptionContent full>
            <ButtonGroup
              items={ContentLoadSettings}
              value={data.config.tabs[selectedTab].enrichment}
              onClick={(value) =>
                updateConfigTab({ value: { enrichment: value } })
              }
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("people-to-display" + (areTabsActive ? "-tabs" : ""))}
        </SettingsOptionHeading>

        {data.config.tabs[selectedTab].enrichment === "dynamic" && <Dynamic />}
        {data.config.tabs[selectedTab].enrichment === "manually" && <Manual />}
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeadingInline>
          <SettingsOptionHeading>{t("view-rights")}</SettingsOptionHeading>
          <SettingsOptionHeadingLight>
            - {t("powered-by-cosmos")}
          </SettingsOptionHeadingLight>
        </SettingsOptionHeadingInline>

        {!isCosmosInstalled || !collections?.length ? (
          <MissingAppPanel
            text={t(
              !isCosmosInstalled
                ? "missing-cosmos-collections-trial"
                : "missing-cosmos-collections"
            )}
            link={
              isCosmosInstalled
                ? `${window.AP._hostOrigin}/wiki/plugins/servlet/ac/com.caelor.confluence.cloud.cosmos/caelor-cosmos-settings?ac.cosmosPage=settings-people_base-collections`
                : "https://marketplace.atlassian.com/apps/1230540/cosmos-intranet-for-confluence-free?hosting=cloud&tab=overview"
            }
            linkText={
              isCosmosInstalled ? t("create-collections") : t("start-trial")
            }
          />
        ) : (
          <OptionContainer direction="column">
            <OptionLabel>{t("users-can-view-this-content")}</OptionLabel>
            <OptionContent full>
              <Select
                placeholder={t("select-collections")}
                value={data.config.tabs[
                  selectedTab
                ].restrictedToCollections.map((id) =>
                  collections.find((c) => c.value === id)
                )}
                options={collections}
                isMulti
                onChange={(result) => {
                  updateConfigTab({
                    value: {
                      restrictedToCollections: result.map((c) => c.value),
                    },
                  });
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
    </>
  );
}

export default ContentSettings;
